import React, { useEffect, useState } from "react";
import CommonSlider from "../../../common/CommonSlider";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  isFieldError,
  isValidPhoneNumber,
  isValidPostalCode,
} from "../../../../helpers/input-validations";
import CustomInput from "../../../common/CustomInput";
import Button from "../../../common/Button";
import { countryOptions, relationshipTypeThirdStepOptions, stateOptions, phoneOptions, addressOption, longTermOption } from "../../create/options";
import { useDispatch, useSelector } from "react-redux";
import { updatePatientContactDetails } from "../../../../redux/modules/patient/actions";
import { extractPhoneNumber } from "../../../../helpers/functions";

const ContactInformationCreate = ({ open, onClose, patient }) => {
  const dispatch = useDispatch();
  const [noPhone, setNoPhone] = useState(patient.noPhone);
  const [primaryPhoneCountry, setPrimaryPhoneCountry] = useState(patient.primaryPhoneCountryCode);
  const [phoneNumber, setPhoneNumber] = useState(patient.primaryPhone);
  const [secondaryPhoneNumber, setSecondaryPhoneNumber] = useState(patient.secondaryPhone);
  const [secondaryPhoneCountry, setSecondaryPhoneCountry] = useState(patient.secondaryPhoneCountryCode);
  const [email, setEmail] = useState(patient.email);
  const [noAddress, setNoAddress] = useState(patient.noAddress);
  const [longTermCare, setLongTermCare] = useState(patient.longTermCare);
  const [facility, setFacility] = useState(patient.nameOfFacility);
  const [address, setAddress] = useState(patient.address);
  const [unit, setUnit] = useState(patient.unitNumber);
  const [poBox, setPoBox] = useState(patient.poBox);
  const [city, setCity] = useState(patient.city);
  const [state, setState] = useState(patient.province);
  const [postalCode, setPostalCode] = useState(patient.postalCode);
  const [country, setCountry] = useState(patient.country);

  const [addressError, setAddressError] = useState(false);
  const [primaryPhoneError, setPrimaryPhoneError] = useState(false);
  const [cityError, setCityError] = useState(false);
  const [stateError, setStateError] = useState(false);
  const [countryError, setCountryError] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState(false);
  const [commentsFirstStep, setCommentsFirstStep] = useState(patient.comments);
  const [clicked, setClicked] = useState(false);
  
  const fieldsToCheck = [
    {
      field: address,
      checkFn: isFieldError,
      setErrorFn: setAddressError,
      alter: false,
      name: "address",
    },
    {
      field: phoneNumber,
      checkFn: isValidPhoneNumber,
      setErrorFn: setPrimaryPhoneError,
      alter: true,
      name:'phoneNumber'
    },
    {
      field: city,
      checkFn: isFieldError,
      setErrorFn: setCityError,
      alter: false,
      name: "city",
    },
    {
      field: state,
      checkFn: isFieldError,
      setErrorFn: setStateError,
      alter: false,
      name: "state",
    },
    {
      field: country,
      checkFn: isFieldError,
      setErrorFn: setCountryError,
      alter: false,
      name: "country",
    },
    {
      field: postalCode,
      checkFn: isValidPostalCode,
      setErrorFn: setPostalCodeError,
      alter: true,
      name: "postalCode",
    },
  ];
  const fullPatient = useSelector((state) => state.patients.patient);
  const primaryCaregiver = fullPatient?.caregiver?.find( (caregiver) => caregiver.contactInfo.primaryContact === true);
  const manageStep = () => {
    setClicked(true);
    let allFieldsValid = true;
    let fieldsToValidate = fieldsToCheck;
    if (noAddress || longTermCare) {
      fieldsToValidate = fieldsToValidate.filter(({ name }) => name !== 'address');
    }
    if (noPhone) {
      fieldsToValidate = fieldsToValidate.filter(
        ({ name }) => name !== 'phoneNumber'
      );
    }
    fieldsToValidate.forEach(({ field, checkFn, setErrorFn, alter }) => {
      const hasError = alter ? !checkFn(field) : checkFn(field);
      setErrorFn(hasError);
      if (hasError) {
        allFieldsValid = false;
      }
    });
    if (allFieldsValid) {
      const transformedObject = {
        noPhone: noPhone,
        primaryPhone: noPhone ? "" : extractPhoneNumber(phoneNumber),
        primaryPhoneCountryCode: noPhone ? "" : primaryPhoneCountry,
        secondaryPhone: noPhone ? "" :  extractPhoneNumber(secondaryPhoneNumber),
        secondaryPhoneCountryCode: noPhone ? "" :  secondaryPhoneCountry,
        email: email,
        noAddress: noAddress,
        longTermCare: longTermCare,
        nameOfFacility: longTermCare ? (facility || null) : "",
        address: noAddress ? "" : address,
        unitNumber: noAddress ? "" : (unit || null),
        poBox: noAddress ? "" : poBox,
        city: city,
        province: state,
        postalCode:  noAddress ? "" : (postalCode && postalCode?.replace(/\s/g, "") || null),
        country: country,
        primaryContact: true,
        comments: commentsFirstStep,
      };
      dispatch(updatePatientContactDetails(patient.id, transformedObject));
      onClose(false);
    }
  };

  useEffect(() => {
    if (clicked) {
      fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
        const hasError = alter ? !checkFn(field) : checkFn(field);
        setErrorFn(hasError);
      });
    }
  }, [clicked, ...fieldsToCheck.map(({ field }) => field)]);

  useEffect(() => {
    if(longTermCare){
      if(noAddress){
      setNoAddress(false)
      }
    }
  }, [longTermCare])

  useEffect(() => {
    if(noAddress){
      if(longTermCare){
      setLongTermCare(false)
      }
    }
  }, [noAddress])

  return (
    <CommonSlider open={open} onClose={onClose}>
      <div className="flex flex-col flex-1 h-full overflow-y-auto ">
        <div className="relative flex-1 flex flex-col">
          {/* Header */}
          <div className="px-8 py-6 bg-gray-20">
          <div className="flex items-start justify-between space-x-3">
              <div className="flex flex-col">
                <p className="font-bold text-gray-800 text-display-xs">
                  {"Edit "} Contact Information
                </p>
                <p className="text-body-md text-gray-700">{fullPatient.lastName}, {fullPatient.firstName}</p>
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                  onClick={() => onClose(false)}
                >
                  <span className="sr-only">Close panel</span>
                  <XMarkIcon
                    className="w-5 h-5 m-auto" 
                    strokeWidth="2"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
          <div className="px-8 flex flex-col flex-1 justify-between mt-4">
            <div className="grid grid-cols-12 gap-3 ">
            <CustomInput
              vertical
              name="noPhone"
              className="col-span-12"
              value={noPhone}
              options={phoneOptions}
              onChange={(e) => setNoPhone(e.target.checked)}
              type="checkbox"
            />

            {noPhone ? null : (
              <>
                <CustomInput
                  name="phoneNumber"
                  className="col-span-6"
                  label="Primary Phone Number"
                  value={phoneNumber}
                  selectValue={primaryPhoneCountry}
                  placeholder="(555) 000-0000"
                  type="phone"
                  required
                  handleSelect={(e) => setPrimaryPhoneCountry(e.target.value)}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  error={primaryPhoneError}
                />
                <CustomInput
                  name="secondaryPhoneNumber"
                  className="col-span-6"
                  label="Secondary Phone Number"
                  placeholder="(555) 000-0000"
                  value={secondaryPhoneNumber}
                  selectValue={secondaryPhoneCountry}
                  type="phone"
                  handleSelect={(e) => setSecondaryPhoneCountry(e.target.value)}
                  onChange={(e) => setSecondaryPhoneNumber(e.target.value)}
                />
              </>
              )}
              <CustomInput
                name="email"
                className="col-span-12"
                label="Email Address"
                value={email}
                placeholder="Enter Email Address"
                type="email"
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="col-span-12 font-medium text-gray-900 text-body-md">
                Address Information
              </p>
              <CustomInput
                horizontal
                name="noAddress"
                className="col-span-6"
                value={noAddress}
                options={addressOption}
                onChange={(e) => setNoAddress(e.target.checked)}
                type="checkbox"
              />
              <CustomInput
                horizontal
                name="longTermCare"
                className="col-span-6"
                value={longTermCare}
                options={longTermOption}
                onChange={(e) => setLongTermCare(e.target.checked)}
                type="checkbox"
              />

              {longTermCare &&( 
              <>
                <CustomInput
                  name="facility"
                  className="col-span-12"
                  value={facility}
                  label="Facility"
                  placeholder="Enter Name of Facility"
                  type="text"
                  onChange={(e) => setFacility(e.target.value)}
                /> 
                <CustomInput
                  name="address"
                  className="col-span-12"
                  value={address}
                  label="Address"
                  placeholder="Enter Street Number & Street Name"
                  type="text"
                  onChange={(e) => setAddress(e.target.value)}
                />     
              </>
              )}
              {noAddress ? null : (
              <>
                {longTermCare ? null : (
                  <CustomInput
                    name="address"
                    className="col-span-12"
                    value={address}
                    label="Address"
                    placeholder=" Enter Street Number & Street Name"
                    type="text"
                    required
                    onChange={(e) => setAddress(e.target.value)}
                    error={addressError}
                  />
                )}
                <CustomInput
                  name="unit"
                  className="col-span-6"
                  value={unit}
                  label="Unit Number"
                  placeholder="Enter Unit Number"
                  type="text"
                  onChange={(e) => setUnit(e.target.value)}
                />
                <CustomInput
                  name="poBox"
                  className="col-span-6"
                  label="PO Box"
                  value={poBox}
                  placeholder="PO Box"
                  type="age"
                  onChange={(e) => setPoBox(e.target.value)}
                />
              </>
              )}
              <CustomInput
                name="city"
                className="col-span-6"
                label="City"
                value={city}
                type="text"
                required
                onChange={(e) => setCity(e.target.value)}
                error={cityError}
              />
              <CustomInput
                options={stateOptions}
                name="state"
                value={state}
                className="col-span-6"
                label="Province"
                type="dropdown"
                required
                onChange={(e) => setState(e.value)}
                error={stateError}
              />
              {noAddress ? null : (
              <>
                <CustomInput
                  name="postalCode"
                  className="col-span-6"
                  label="Postal Code"
                  value={postalCode}
                  placeholder="Enter Postal Code"
                  type="postalCode"
                  onChange={(e) => setPostalCode(e.target.value)}
                  error={postalCodeError}
                />
              </>
              )}
              <CustomInput
                options={countryOptions}
                name="countryOptions"
                className="col-span-6"
                value={country}
                label="Country"
                type="dropdown"
                required
                onChange={(e) => setCountry(e.value)}
                error={countryError}
              />
              <p className="col-span-12 font-medium text-gray-900 text-body-md">
                Primary Contact Relationship
              </p>
              <CustomInput 
                label={'Primary Contact'}
                className="col-span-6  "
                name={'primaryContact'}
                type={'text'}
                required
                disabled
                value={`${primaryCaregiver?.lastName}, ${primaryCaregiver?.firstName}`}
              />
              <CustomInput
                options={relationshipTypeThirdStepOptions}
                name="RelationshipTypeCaregiver"
                className="col-span-6"
                label="Relationship"
                type="dropdown"
                value={primaryCaregiver?.relationship}
                disabled
                required
              />
              <p className="text-body-sm col-span-12 mb-2 text-teal-300">To add/edit Primary Contact, use the Alternate Caregiver tab</p>
              <CustomInput
                name="commentsFirstStep"
                className="col-span-12"
                label="Comments"
                value={commentsFirstStep}
                placeholder="Enter Note"
                type="text"
                onChange={(e) => setCommentsFirstStep(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-center py-6">
              <div className="flex items-center justify-center space-x-5">
                <Button
                  className="min-w-[200px]"
                  text={`Cancel`}
                  type="secondary"
                  onClick={() => onClose(false)}
                />
                <Button
                  className="min-w-[200px]"
                  text="Update"
                  type="primary"
                  onClick={() => manageStep()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </CommonSlider>
  );
};

export default ContactInformationCreate;
